<template>
    <div
        :class="{
            'lead-list-pipeline-category-filter': true,
        }"
    >
        <div
            v-for="i in rowCount"
            :key="i"
            :class="{
                'lead-list-pipeline-category-filter__row': true,
                'lead-list-pipeline-category-filter__row--full': getCategoriesForRow(i).length === getMaxItemsForRow(i),
                'lead-list-pipeline-category-filter__row--first': i === 1,
                'lead-list-pipeline-category-filter__row--last': i === rowCount,
            }"
        >
            <button
                v-if="i === 1"
                :class="{
                    'lead-list-pipeline-category-filter__item': true,
                    'lead-list-pipeline-category-filter__item--active': props.activePipelineCategory === null,
                }"
                type="button"
                @click="emit('filter-pipeline-category', null)"
            >
                <text-decorator :size="FontSize.md" weight="bold">
                    Alles
                </text-decorator>
                <chip-informative :color="props.activePipelineCategory === null ? 'gray-800' : 'primary'">
                    {{ counts?.total ?? 0 }}
                </chip-informative>
            </button>
            <button
                v-for="category in getCategoriesForRow(i)"
                :key="category.value"
                :class="{
                    'lead-list-pipeline-category-filter__item': true,
                    'lead-list-pipeline-category-filter__item--active': props.activePipelineCategory?.equals(category),
                }"
                type="button"
                @click="$emit('filter-pipeline-category', category)"
            >
                <text-decorator :size="FontSize.md" weight="bold">
                    {{ category.label }}
                </text-decorator>
                <chip-informative :color="props.activePipelineCategory?.equals(category) ? 'primary' : 'gray-800' ">
                    {{ countForCategory(category) }}
                </chip-informative>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type CountLeadsCommandResponse, PipelineCategory, Pipeline } from '@keyvue/one-api-client';
    import type { ReactiveCollection } from '@/services/keyvue-api/composables/useClient';
    import { ChipInformative, FontSize } from '@keyvue/one-ui';
    import { computed, inject, type Ref } from 'vue';

    const props = defineProps<{
        pipelineCollection: ReactiveCollection<Pipeline>,
        activePipelineCategory: PipelineCategory | null,
    }>();

    const counts = inject<Ref<CountLeadsCommandResponse>>('counts');

    function countForCategory(category: PipelineCategory) {
        return computed(() => {
            return counts?.value.pipelineCategories?.find((count) => count.category === category.value)?.count ?? 0;
        });
    }

    const categories = computed(() => {
        return PipelineCategory.winckValues
            .filter((category) => {
                return props.pipelineCollection.data.value.some((pipeline) => pipeline.category.equals(category));
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    });

    const firstRowMaxItems = 4;
    const maxRowItems = 5;
    const rowCount = computed(() => {
        const totalCategories = categories.value.length;
        const remainingCategories = totalCategories - firstRowMaxItems;

        return 1 + Math.ceil(remainingCategories / maxRowItems);
    });

    function getMaxItemsForRow(rowIndex: number) {
        return rowIndex === 1 ? firstRowMaxItems : maxRowItems;
    }
    function getCategoriesForRow(rowIndex: number) {
        if (rowIndex === 1) {
            return categories.value.slice(0, firstRowMaxItems);
        }
        const start = firstRowMaxItems + (rowIndex - 2) * maxRowItems;
        const end = start + maxRowItems;
        return categories.value.slice(start, end);
    }

    const emit = defineEmits<{
        (e: 'filter-pipeline-category', value: PipelineCategory | null): void;
    }>();
</script>

<style scoped lang="scss">
.lead-list-pipeline-category-filter {
    display: flex;
    flex-wrap: wrap;

    &__item {
        display: flex;
        gap: size(sm);
        align-items: center;
        padding: size(sm) size(xl);
        border:0;
        background-color: color(brand-white);
        justify-content: center;
        transition: background-color .2s;
        cursor: pointer;

        &--active {
            background-color: var(--theme-colors-default--primary);
            color: color(brand-white);
        }
    }

    &__row {
        display: flex;
        overflow: hidden;
        border-left: 1px solid color(gray-300);
        border-right: 1px solid color(gray-300);
        border-bottom: 1px solid color(gray-300);
        background-color: color(gray-300);
        gap: 1px;

        &--full {
            width: 100%;

            & .lead-list-pipeline-category-filter__item {
                flex-grow: 1;
            }
        }

        &--first {
            border-top: 1px solid color(gray-300);
            border-top-right-radius: size(sm);
            border-top-left-radius: size(sm);
            border-bottom-right-radius: 0;
        }

        &--second-last {
            border-bottom-right-radius: size(sm);
        }

        &--last {
            border-bottom-right-radius: size(sm);
            border-bottom-left-radius: size(sm);
        }
    }
}
</style>
